import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import Brand from "../components/Brand";
import Plate from "../components/Plate";
import SbrFooter from "../components/SbrFooter";
import SlCloudNav from "../brands/servicelad/SlCloudNav";
import SlCloudFooter from "../brands/servicelad/SlCloudFooter";
import usePageMeta from "../hooks/usePageMeta";
import { getVehicle } from "../services/vehicleApi";
import { createOrder, createPaymentIntent, confirmPayment } from "../services/paymentApi";
import checkoutCss from "../styles/checkout.css?raw";
import { findMakeByDbMake } from "../../lib/vehicle/makes.js";
import { getWalletRecoveryAction } from "../utils/paymentRecovery";
import { cleanRegistration, formatUkRegistration, isValidUkRegistration } from "../utils/registration";
import { packagesFor } from "../config/pricing.js";

const defaultPackages = [
  {
    key: "risk",
    name: "Vehicle Risk Check",
    price: 9.99,
    description: "Know the risks before you buy. Stolen, write-off and finance checks in one report.",
    items: ["Stolen vehicle check", "Insurance write-off check", "Outstanding finance check", "Vehicle valuation where available", "Export and scrap checks", "Mileage verification"],
  },
  {
    key: "complete",
    name: "Complete History Report",
    price: 16.99,
    description: "The full picture. Everything in the Risk Check plus the manufacturer's own service history.",
    items: ["Everything in Risk Check", "Vehicle valuation where available", "Manufacturer service history", "Recorded service actions", "Garage details when available"],
    popular: true,
  },
];

// ServiceLad's names, prices and wording come from the shared catalogue.
function checkoutPackagesFor(brandKey) {
  if (brandKey !== "servicelad") return defaultPackages;
  const [risk, complete] = packagesFor(brandKey);
  return [
    { ...defaultPackages[0], name: risk.name, price: risk.pricePence / 100, description: "The full HPI check: stolen, write-off and finance checks in one report." },
    { ...defaultPackages[1], name: complete.name, price: complete.pricePence / 100, description: "Everything in the full HPI check plus the manufacturer's own service history.", items: ["Everything in the HPI check", ...defaultPackages[1].items.slice(1)] },
  ];
}

const v5cTerms = [
  { months: 3, price: 1.49, note: "Starter" },
  { months: 6, price: 2.49, note: "Save 16%" },
  { months: 12, price: 3.99, note: "Best value" },
];

// DVLA sends upper case; show the maker's own name and a readable model.
function displayMake(make) {
  return findMakeByDbMake(make)?.name || String(make).toLowerCase().replace(/(^|[\s-])([a-z])/g, (m, p, c) => p + c.toUpperCase());
}
function displayModel(model) {
  return String(model).replace(/[A-Z]+/g, (word) => (word.length <= 3 ? word : word[0] + word.slice(1).toLowerCase()));
}

export default function CheckoutPage({ brand }) {
  const packages = checkoutPackagesFor(brand.key);
  const riskName = packages[0].name;
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const rawVrm = cleanRegistration(params.get("vrm") || "");
  const displayVrm = formatUkRegistration(rawVrm);
  const [vehicle, setVehicle] = useState(null);
  const [vehicleLoading, setVehicleLoading] = useState(true);
  const [vehicleErrorMessage, setVehicleErrorMessage] = useState("");
  const [vehicleNotFound, setVehicleNotFound] = useState(false);
  const requestedPlan = (params.get("plan") || "").toLowerCase();
  // ServiceByReg and ServiceLad also sell V5C alerts with no report.
  const v5cOnlyAllowed = brand.key === "servicebyreg" || brand.key === "servicelad";
  const initialPackage = v5cOnlyAllowed && requestedPlan === "v5c"
    ? "v5c"
    : packages.find((plan) => requestedPlan.includes(plan.key))?.key || "complete";
  const [selectedPackage, setSelectedPackage] = useState(initialPackage);
  const [v5cSelected, setV5cSelected] = useState(() => {
    if (initialPackage === "v5c") return true;
    const requestedV5c = params.get("v5c");
    if (requestedV5c !== null) return requestedV5c !== "0";
    // Free-page upsell leaves V5C off unless the buyer ticks it.
    return false;
  });
  const requestedV5cTerm = Number(params.get("v5c"));
  const [v5cTerm, setV5cTerm] = useState(v5cTerms.some((term) => term.months === requestedV5cTerm) ? requestedV5cTerm : 3);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paid, setPaid] = useState(false);
  // From the server, so it is right after a PayPal or bank redirect, when the page reloads without the plan.
  const [paidV5cOnly, setPaidV5cOnly] = useState(false);
  const [payError, setPayError] = useState("");
  const [payLoading, setPayLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [stripeInstance, setStripeInstance] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [elements, setElements] = useState(null);
  const [expressAvailable, setExpressAvailable] = useState(false);
  const [returningPayment, setReturningPayment] = useState(false);
  const [returnPaymentError, setReturnPaymentError] = useState("");
  const [historyChecking, setHistoryChecking] = useState(false);
  const [noHistoryResult, setNoHistoryResult] = useState(null);
  const [partialResult, setPartialResult] = useState(null);
  const [serviceHistoryUnavailable, setServiceHistoryUnavailable] = useState(false);
  const [walletMountVersion, setWalletMountVersion] = useState(0);
  const expressCheckoutMount = useRef(null);
  const paymentElementMount = useRef(null);
  const cardElementRef = useRef(null);
  const emailInput = useRef(null);
  const paymentBusy = useRef(false);
  const walletRecoveryBusy = useRef(false);
  const walletWasOpened = useRef(false);
  const [showVehicleChange, setShowVehicleChange] = useState(!rawVrm);
  const [newVrm, setNewVrm] = useState("");
  const [vehicleError, setVehicleError] = useState("");
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");
  const [voucherNote, setVoucherNote] = useState("");

  useEffect(() => {
    // No reg in the link: ask for one rather than selling a report for a guessed car.
    if (!rawVrm) {
      setVehicleLoading(false);
      setVehicleNotFound(true);
      setVehicleErrorMessage("Enter a registration to choose a report.");
      return undefined;
    }
    let active = true;
    setVehicleLoading(true);
    setVehicleNotFound(false);
    getVehicle(rawVrm)
      .then((data) => {
        if (!active) return;
        setVehicle(data);
        setVehicleErrorMessage("");
      })
      .catch((error) => {
        if (!active) return;
        setVehicleNotFound(Boolean(error?.notFound));
        setVehicleErrorMessage(error?.notFound
          ? "DVLA and DVSA have no record of this registration. Check it and change the vehicle."
          : "Vehicle details are temporarily unavailable.");
      })
      .finally(() => {
        if (active) setVehicleLoading(false);
      });
    return () => { active = false; };
  }, [rawVrm]);

  // Manufacturer service history only covers certain makes from 2012
  // onward (see src/services/vehicleApi.js). Don't let a customer keep a
  // Complete History Report selected for a vehicle that will never have a
  // service-history section to show.
  useEffect(() => {
    if ((vehicle?.serviceHistoryEligible === false || vehicle?.serviceHistoryKnownEmpty || serviceHistoryUnavailable) && selectedPackage === "complete") {
      setSelectedPackage("risk");
    }
  }, [vehicle, selectedPackage, serviceHistoryUnavailable]);

  usePageMeta({
    title: `Choose your report | ${brand.name}`,
    description: `Choose a vehicle-history report for ${rawVrm}.`,
    robots: "noindex, follow",
  });

  const v5cOnly = selectedPackage === "v5c";
  const packageChoice = packages.find((plan) => plan.key === selectedPackage) || packages[0];
  // A ?v5c= value outside the three sold terms must not leave this undefined:
  // every price line below reads it, so a stray link would blank the page.
  const chosenV5c = v5cTerms.find((term) => term.months === v5cTerm) || v5cTerms[0];
  const total = (v5cOnly ? 0 : packageChoice.price) + (v5cSelected || v5cOnly ? chosenV5c.price : 0);

  const applyVoucher = () => {
    const code = voucherCode.trim();
    if (!code) {
      setVoucherNote("Enter a code first.");
      return;
    }
    setVoucherNote("This code is not valid or has expired.");
  };

  const buildOrderItems = () => {
    const items = v5cOnly ? [] : [{ item_type: "package", label: packageChoice.name, price_pence: Math.round(packageChoice.price * 100) }];
    if (v5cSelected || v5cOnly) {
      items.push({ item_type: "v5c_alerts", label: `V5C alerts (${v5cTerm} months)`, price_pence: Math.round(chosenV5c.price * 100), duration_months: v5cTerm });
    }
    return items;
  };

  const finishPaidOrder = useCallback(async (paidOrderId, paymentIntentId) => {
    setHistoryChecking(true);
    try {
      for (let attempt = 0; attempt < 12; attempt += 1) {
        const confirmResult = await confirmPayment(paidOrderId, paymentIntentId);
        if (["no_history", "history_unavailable", "canceled"].includes(confirmResult.status)) {
          setServiceHistoryUnavailable(true);
          setNoHistoryResult({
            status: confirmResult.status,
            message: confirmResult.message || "Your payment was not taken.",
          });
          return;
        }
        // No service records, but the rest of the order was still owed, so part
        // of the authorisation was taken and the balance released. Say so
        // before handing over the report rather than redirecting silently.
        if (confirmResult.status === "partially_paid") {
          setPartialResult({
            serviceHistoryStatus: confirmResult.service_history_status,
            capturedPence: confirmResult.captured_pence,
            releasedPence: confirmResult.released_pence,
            message: confirmResult.message,
            reportToken: confirmResult.report_token || null,
          });
          return;
        }
        if (confirmResult.report_token) {
          window.location.href = `/report?token=${confirmResult.report_token}`;
          return;
        }
        if (confirmResult.status === "paid") {
          setPaidV5cOnly(Boolean(confirmResult.v5c_only));
          setPaid(true);
          return;
        }
        if (confirmResult.status !== "checking") break;
        await new Promise((resolve) => window.setTimeout(resolve, 1250));
      }
      throw new Error("The service-record check is taking longer than expected. Your payment has not been taken yet.");
    } finally {
      setHistoryChecking(false);
    }
  }, []);

  const handleCheckout = async () => {
    if (vehicleLoading) return;
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      setEmailError("Enter your email address.");
      emailInput.current?.focus();
      return;
    }
    // The browser accepts "name@gmail" with no ending, so also require a dot in the domain.
    if ((emailInput.current && !emailInput.current.validity.valid) || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(cleanEmail)) {
      setEmailError("Enter a valid email address.");
      emailInput.current.focus();
      return;
    }

    setEmailError("");
    setPayError("");
    setReturnPaymentError("");
    setNoHistoryResult(null);
    setExpressAvailable(false);
    setPayLoading(true);
    try {
      const orderItems = buildOrderItems();
      const totalPence = Math.round(total * 100);
      const order = await createOrder({
        email: cleanEmail,
        registration: rawVrm,
        brand: brand.key,
        totalPence,
        items: orderItems,
      });
      setOrderId(order.order_id);

      const { client_secret, publishable_key } = await createPaymentIntent(order.order_id);
      setClientSecret(client_secret);

      const stripe = await loadStripe(publishable_key || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);
      setStripeInstance(stripe);

      const accent = {
        serviceboss: "#ff5c35",
        servicebyreg: "#1f6fff",
        servicelad: "#3b82f6",
      }[brand.key] || "#101418";
      const els = stripe.elements({
        clientSecret: client_secret,
        locale: "en-GB",
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: accent,
            colorText: "#101418",
            colorTextSecondary: "#666d73",
            borderRadius: "10px",
            fontFamily: '"DM Sans", Arial, sans-serif',
            fontSizeBase: "16px",
            spacingUnit: "4px",
          },
        },
      });
      setElements(els);

      setPaymentOpen(true);
    } catch (err) {
      setPayError(err.message || "Failed to start checkout");
    } finally {
      setPayLoading(false);
    }
  };

  const handlePayPalPayment = useCallback(async () => {
    if (!stripeInstance || !clientSecret || !orderId || paymentBusy.current) return;
    paymentBusy.current = true;
    walletWasOpened.current = true;
    setPayError("");
    setPayLoading(true);
    try {
      const returnUrl = new URL("/checkout", window.location.origin);
      returnUrl.searchParams.set("vrm", rawVrm);
      returnUrl.searchParams.set("order", orderId);

      // Own PayPal button — do not rely on Stripe painting a tab inside the iframe.
      const { error } = await stripeInstance.confirmPayPalPayment(clientSecret, {
        return_url: returnUrl.toString(),
      });
      if (error) throw error;
    } catch (err) {
      setPayError(err.message || "PayPal payment failed");
      walletWasOpened.current = false;
    } finally {
      paymentBusy.current = false;
      setPayLoading(false);
    }
  }, [clientSecret, orderId, rawVrm, stripeInstance]);

  const handleWalletConfirm = useCallback(async () => {
    if (!stripeInstance || !elements || !clientSecret || !orderId || paymentBusy.current) return;
    paymentBusy.current = true;
    setPayError("");
    setPayLoading(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) throw submitError;

      const returnUrl = new URL("/checkout", window.location.origin);
      returnUrl.searchParams.set("vrm", rawVrm);
      returnUrl.searchParams.set("order", orderId);

      const { error: confirmError, paymentIntent } = await stripeInstance.confirmPayment({
        elements,
        clientSecret,
        confirmParams: { return_url: returnUrl.toString() },
        redirect: "if_required",
      });

      if (confirmError) throw confirmError;
      if (["succeeded", "requires_capture"].includes(paymentIntent?.status)) {
        await finishPaidOrder(orderId, paymentIntent.id);
      } else if (paymentIntent?.status === "processing") {
        setPayError("Your payment is still processing. Please wait.");
      }
    } catch (err) {
      setPayError(err.message || "Payment failed");
    } finally {
      paymentBusy.current = false;
      setPayLoading(false);
    }
  }, [clientSecret, elements, finishPaidOrder, orderId, rawVrm, stripeInstance]);

  const handleCardPayment = useCallback(async () => {
    if (!stripeInstance || !cardElementRef.current || !clientSecret || !orderId || paymentBusy.current) return;
    paymentBusy.current = true;
    setPayError("");
    setPayLoading(true);
    try {
      const { error, paymentIntent } = await stripeInstance.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElementRef.current,
          billing_details: { email: email.trim() },
        },
      });
      if (error) throw error;
      if (["succeeded", "requires_capture"].includes(paymentIntent?.status)) {
        await finishPaidOrder(orderId, paymentIntent.id);
      } else if (paymentIntent?.status === "processing") {
        setPayError("Your payment is still processing. Please wait.");
      }
    } catch (err) {
      setPayError(err.message || "Payment failed");
    } finally {
      paymentBusy.current = false;
      setPayLoading(false);
    }
  }, [clientSecret, email, finishPaidOrder, orderId, stripeInstance]);

  const recoverWalletState = useCallback(async () => {
    if (!walletWasOpened.current || !stripeInstance || !clientSecret || !orderId || paymentBusy.current || walletRecoveryBusy.current) return;

    walletRecoveryBusy.current = true;
    try {
      const { paymentIntent, error } = await stripeInstance.retrievePaymentIntent(clientSecret);
      if (error) throw error;

      const action = getWalletRecoveryAction(paymentIntent?.status);
      if (action === "complete") {
        await finishPaidOrder(orderId, paymentIntent.id);
        return;
      }
      if (action === "wait") {
        setPayLoading(true);
        setPayError("Your payment is still processing. Please wait.");
        return;
      }
      if (action === "expired") {
        setPayLoading(false);
        setPayError("This payment session has expired. Close this window and start payment again.");
        return;
      }
      if (action === "reset") {
        setPayLoading(false);
        setPayError("");
        setExpressAvailable(false);
        // Only the wallet buttons are remounted. The card form and order stay in place.
        setWalletMountVersion((version) => version + 1);
      }
    } catch {
      setPayLoading(false);
      setPayError("The wallet window closed. You can try again or pay by card.");
      setExpressAvailable(false);
      setWalletMountVersion((version) => version + 1);
    } finally {
      walletWasOpened.current = false;
      walletRecoveryBusy.current = false;
    }
  }, [clientSecret, finishPaidOrder, orderId, stripeInstance]);

  useEffect(() => {
    if (!paymentOpen || paid || returningPayment || historyChecking || noHistoryResult || partialResult || returnPaymentError) return undefined;
    if (!stripeInstance || !elements || !clientSecret || !orderId) return undefined;
    if (!expressCheckoutMount.current) return undefined;

    setExpressAvailable(false);
    // iPhone/iPad: Apple Pay beside PayPal. Android + desktop: Google Pay beside PayPal.
    const isAppleMobile = /iPhone|iPad|iPod/i.test(navigator.userAgent)
      || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    const updateExpressAvailability = (event) => {
      const methods = event?.availablePaymentMethods || {};
      const hasWalletButton = Boolean(methods.applePay || methods.googlePay);
      if (!hasWalletButton) {
        setExpressAvailable(false);
        return;
      }
      setExpressAvailable(true);
      window.requestAnimationFrame(() => {
        const frame = expressCheckoutMount.current?.querySelector("iframe");
        const height = frame?.getBoundingClientRect?.().height || 0;
        if (height < 24) setExpressAvailable(false);
      });
    };

    let expressElement;
    try {
      expressElement = elements.create("expressCheckout", {
        business: { name: brand.name },
        billingAddressRequired: false,
        phoneNumberRequired: false,
        shippingAddressRequired: false,
        buttonHeight: 48,
        buttonTheme: {
          applePay: "black",
          googlePay: "black",
        },
        buttonType: {
          applePay: "plain",
          googlePay: "plain",
        },
        layout: { maxColumns: 1, maxRows: 1, overflow: "auto" },
        lineItems: buildOrderItems().map((item) => ({
          name: item.label,
          amount: item.price_pence,
        })),
        paymentMethodOrder: isAppleMobile ? ["apple_pay"] : ["google_pay"],
        paymentMethods: {
          applePay: isAppleMobile ? "always" : "never",
          googlePay: isAppleMobile ? "never" : "always",
          // PayPal is our own yellow button in the same row.
          paypal: "never",
          link: "never",
          amazonPay: "never",
          klarna: "never",
        },
      });
    } catch (err) {
      console.error("Express Checkout setup failed:", err);
      setExpressAvailable(false);
      return undefined;
    }

    expressElement.on("ready", updateExpressAvailability);
    expressElement.on("availablepaymentmethodschange", updateExpressAvailability);
    expressElement.on("click", (event) => {
      walletWasOpened.current = true;
      event.resolve();
    });
    expressElement.on("confirm", handleWalletConfirm);
    expressElement.on("cancel", recoverWalletState);
    expressElement.on("loaderror", () => setExpressAvailable(false));
    expressElement.mount(expressCheckoutMount.current);

    return () => expressElement.destroy();
  }, [paymentOpen, paid, returningPayment, historyChecking, noHistoryResult, partialResult, returnPaymentError, stripeInstance, elements, clientSecret, orderId, brand.name, handleWalletConfirm, recoverWalletState, walletMountVersion]);

  useEffect(() => {
    if (!paymentOpen || paid || returningPayment || historyChecking || noHistoryResult || partialResult || returnPaymentError) return undefined;
    if (!stripeInstance || !paymentElementMount.current) return undefined;

    // Card Element only — Payment Element was also showing a second PayPal tab.
    const accent = {
      serviceboss: "#ff5c35",
      servicebyreg: "#1f6fff",
      servicelad: "#3b82f6",
    }[brand.key] || "#101418";
    let cardElement;
    try {
      const cardElements = stripeInstance.elements({
        locale: "en-GB",
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: accent,
            colorText: "#101418",
            colorTextSecondary: "#666d73",
            borderRadius: "10px",
            fontFamily: '"Figtree", "DM Sans", Arial, sans-serif',
            fontSizeBase: "16px",
            spacingUnit: "4px",
          },
        },
      });
      cardElement = cardElements.create("card", {
        hidePostalCode: true,
        style: {
          base: {
            color: "#101418",
            fontFamily: "DM Sans, Arial, sans-serif",
            fontSize: "16px",
            "::placeholder": { color: "#9aa0a6" },
          },
        },
      });
      cardElement.mount(paymentElementMount.current);
      cardElementRef.current = cardElement;
    } catch (err) {
      console.error("Card Element setup failed:", err);
      setPayError("Payment form could not load. Please reload and try again.");
      return undefined;
    }

    return () => {
      cardElementRef.current = null;
      cardElement.destroy();
    };
  }, [paymentOpen, paid, returningPayment, historyChecking, noHistoryResult, partialResult, returnPaymentError, stripeInstance, brand.key]);

  useEffect(() => {
    if (!paymentOpen || paid || returningPayment || returnPaymentError) return undefined;
    if (!stripeInstance || !clientSecret || !orderId) return undefined;

    let returnTimer;
    const checkAfterReturn = () => {
      if (document.visibilityState !== "visible") return;
      if (!walletWasOpened.current) return;
      window.clearTimeout(returnTimer);
      returnTimer = window.setTimeout(recoverWalletState, 750);
    };

    window.addEventListener("focus", checkAfterReturn);
    window.addEventListener("pageshow", checkAfterReturn);
    document.addEventListener("visibilitychange", checkAfterReturn);

    return () => {
      window.clearTimeout(returnTimer);
      window.removeEventListener("focus", checkAfterReturn);
      window.removeEventListener("pageshow", checkAfterReturn);
      document.removeEventListener("visibilitychange", checkAfterReturn);
    };
  }, [paymentOpen, paid, returningPayment, returnPaymentError, stripeInstance, clientSecret, orderId, recoverWalletState]);

  useEffect(() => {
    const returnedPaymentIntent = params.get("payment_intent");
    const returnedOrderId = params.get("order");
    if (!returnedPaymentIntent || !returnedOrderId) return undefined;

    let active = true;
    setPaymentOpen(true);
    setReturningPayment(true);
    setReturnPaymentError("");

    finishPaidOrder(returnedOrderId, returnedPaymentIntent)
      .catch((err) => {
        if (active) setReturnPaymentError(err.message || "We could not confirm the payment status.");
      })
      .finally(() => {
        if (active) setReturningPayment(false);
      });

    return () => { active = false; };
  }, [finishPaidOrder, params]);

  const changeVehicle = (event) => {
    event.preventDefault();
    const cleanVrm = cleanRegistration(newVrm);
    if (!isValidUkRegistration(cleanVrm)) {
      setVehicleError("Enter a valid UK registration.");
      return;
    }
    // Keep the report and V5C choice the buyer already made.
    const next = new URLSearchParams({ vrm: cleanVrm, plan: selectedPackage, v5c: v5cSelected ? String(v5cTerm) : "0" });
    window.location.href = `/checkout?${next}`;
  };

  // A stray tap outside the card must not hide a payment in progress or its result.
  const paymentFormShown = !(paid || returningPayment || historyChecking || partialResult || noHistoryResult || returnPaymentError);

  const chooseRiskInstead = () => {
    setPaymentOpen(false);
    setSelectedPackage("risk");
    setNoHistoryResult(null);
    setPayError("");
    setClientSecret("");
    setOrderId(null);
    setElements(null);
    setStripeInstance(null);
  };

  return (
    <div className={brand.key === "servicelad" ? "sl-cloud relative min-h-screen" : undefined}>
      {brand.key === "servicelad" ? <div className="atmosphere" aria-hidden /> : null}
      <style>{checkoutCss}</style>
      {brand.key === "servicelad" ? (
        <SlCloudNav brand={brand} ctaHref="/#lookup" ctaLabel="Check a vehicle" />
      ) : (
        <header>
          <div className="wrap nav">
            <Brand brand={brand} />
          </div>
        </header>
      )}

      <main className={brand.key === "servicelad" ? "relative z-10" : undefined}>
        <div className="wrap">
          <div className="steps"><div className="step active"><span>1</span>Choose report</div><div className="step-line" /><div className="step"><span>2</span>Payment</div><div className="step-line" /><div className="step"><span>3</span>Your report</div></div>
          <div className="page-grid">
            <section>
              {vehicle && <div className="eyebrow">Vehicle found</div>}
              <h1>Choose your history check.</h1>
              <p className="lead">Select how much detail you need. Every report is a one-off purchase with no subscription.</p>

              <div className="vehicle-bar">
                <div className="vehicle-plate-stack">
                  <Plate registration={rawVrm} />
                  <button className="change" onClick={() => setShowVehicleChange((current) => !current)}>Change vehicle</button>
                </div>
                <div className="vehicle-copy">
                  <strong>{vehicleLoading ? "Loading vehicle details..." : vehicle?.make && vehicle?.model ? `${displayMake(vehicle.make)} ${displayModel(vehicle.model)}` : !rawVrm ? "No registration entered" : vehicleNotFound ? "Registration not found" : "Vehicle details unavailable"}</strong>
                  <small>{vehicle ? [vehicle.year, vehicle.fuelType, vehicle.colour].filter(Boolean).join(" · ") : vehicleErrorMessage || "Checking DVLA and DVSA records"}</small>
                </div>
              </div>
              {showVehicleChange && (
                <form className="change-vehicle-form" onSubmit={changeVehicle}>
                  <div className="change-plate">
                    <span><img src="/gb-flag.svg" alt="" />UK</span>
                    <input
                      aria-label="New vehicle registration"
                      value={newVrm}
                      maxLength="8"
                      placeholder="ENTER REG"
                      autoFocus
                      onChange={(event) => {
                        setNewVrm(cleanRegistration(event.target.value));
                        setVehicleError("");
                      }}
                    />
                  </div>
                  <button type="submit">Go</button>
                  {vehicleError && <small>{vehicleError}</small>}
                </form>
              )}

              <div className="options">
                {packages.map((plan) => {
                  const disabled = plan.key === "complete" && (vehicle?.serviceHistoryEligible === false || vehicle?.serviceHistoryKnownEmpty || serviceHistoryUnavailable);
                  const disabledDescription = vehicle?.serviceHistoryKnownEmpty || serviceHistoryUnavailable
                    ? `Service records were not found or could not be confirmed for this vehicle. Choose the ${riskName} instead.`
                    : `Manufacturer service history isn't available for this vehicle. ${brand.name} covers select manufacturers from 2012 onward; this one falls outside that.`;
                  return (
                    <label className={`option${disabled ? " is-disabled" : ""}`} key={plan.key}>
                      <input type="radio" name="report" disabled={disabled} checked={!disabled && selectedPackage === plan.key} onChange={() => setSelectedPackage(plan.key)} />
                      <span className="option-inner">
                        <span className="radio" />
                        <span>
                          <h3>{plan.name} {plan.popular && !disabled && <small className="tag">Best value</small>}</h3>
                          <p>{disabled ? disabledDescription : plan.description}</p>
                          <ul>{plan.items.map((item) => <li key={item}>{item}</li>)}</ul>
                        </span>
                        <strong className="option-price">£{plan.price.toFixed(2)}</strong>
                      </span>
                    </label>
                  );
                })}
                {v5cOnlyAllowed && (
                  <label className="option">
                    <input type="radio" name="report" checked={v5cOnly} onChange={() => { setSelectedPackage("v5c"); setV5cSelected(true); }} />
                    <span className="option-inner">
                      <span className="radio" />
                      <span>
                        <h3>V5C alerts only</h3>
                        <p>No report. We check the V5C date DVLA holds every day and email you if it changes. Choose 3, 6 or 12 months below.</p>
                        <ul><li>Daily DVLA check</li><li>Email the day it changes</li><li>One payment, no renewal</li></ul>
                      </span>
                      <strong className="option-price">from £{v5cTerms[0].price.toFixed(2)}</strong>
                    </span>
                  </label>
                )}
              </div>

              <div className="custom-builder active">
                <div className="builder-head">
                  <div><h2>{v5cOnly ? "How long to watch" : "Add V5C monitoring"}</h2><p>{v5cOnly ? "One payment for the whole term. It does not renew." : "Watch for logbook changes over a period of time. Available with either report."}</p></div>
                </div>
                <div className="service-list">
                  <div className="v5c-block">
                    <label className="service">
                      <input className="service-input" type="checkbox" checked={v5cSelected || v5cOnly} disabled={v5cOnly} onChange={(event) => setV5cSelected(event.target.checked)} />
                      <span className="service-check">✓</span><span><strong>V5C alerts</strong><small>Monitor logbook changes for a period of time</small></span><span className="service-price">{v5cSelected || v5cOnly ? "Added" : "from £1.49"}</span>
                    </label>
                    {(v5cSelected || v5cOnly) && (
                      <div className="v5c-duration">
                        {v5cTerms.map((term) => (
                          <label className="duration-option" key={term.months}>
                            <input type="radio" name="v5c-term" checked={v5cTerm === term.months} onChange={() => setV5cTerm(term.months)} />
                            <span className="duration-card"><b>{term.months} months</b><strong>£{term.price.toFixed(2)}</strong><small>{term.note}</small></span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <aside className="summary">
              <h2>Order summary</h2>
              <div className="summary-row">
                <div><strong>{v5cOnly ? `V5C alerts (${v5cTerm} months)` : packageChoice.name}</strong><p>For this {vehicle?.make ? `${displayMake(vehicle.make)} - ` : ""}{displayVrm.replace(/\s+$/, "")}</p></div><strong>£{(v5cOnly ? chosenV5c.price : packageChoice.price).toFixed(2)}</strong>
              </div>
              {v5cSelected && !v5cOnly && (
                <div className="summary-items">
                  <div className="summary-item"><span>V5C alerts ({v5cTerm} months)</span><strong>£{chosenV5c.price.toFixed(2)}</strong></div>
                </div>
              )}
              <div className="voucher-block">
                {!voucherOpen ? (
                  <button type="button" className="voucher-toggle" onClick={() => setVoucherOpen(true)}>
                    Have a voucher code?
                  </button>
                ) : (
                  <div className="voucher-row">
                    <div className="voucher-head">
                      <label htmlFor="voucher-code">Voucher code</label>
                      <button
                        type="button"
                        className="voucher-close"
                        aria-label="Close voucher code"
                        onClick={() => {
                          setVoucherOpen(false);
                          setVoucherCode("");
                          setVoucherNote("");
                        }}
                      >
                        &times;
                      </button>
                    </div>
                    <div className="voucher-input-row">
                      <input
                        id="voucher-code"
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        placeholder="Enter code"
                        value={voucherCode}
                        onChange={(event) => {
                          setVoucherCode(event.target.value.toUpperCase());
                          if (voucherNote) setVoucherNote("");
                        }}
                      />
                      <button type="button" onClick={applyVoucher}>Apply</button>
                    </div>
                    {voucherNote && <p className="voucher-note" role="status">{voucherNote}</p>}
                  </div>
                )}
              </div>
              <div className="total"><span>Total to pay</span><strong>£{total.toFixed(2)}</strong></div>
              <div className={`field${emailError ? " has-error" : ""}`}>
                <label htmlFor="email">{v5cOnly ? "EMAIL FOR YOUR ALERTS" : "EMAIL FOR YOUR REPORT"}</label>
                <input
                  ref={emailInput}
                  id="email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  aria-invalid={Boolean(emailError)}
                  aria-describedby={emailError ? "email-error" : undefined}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    if (emailError) setEmailError("");
                  }}
                />
                {emailError && <small id="email-error" className="field-error" role="alert">{emailError}</small>}
              </div>
              {payError && <div className="pay-error">{payError}</div>}
              <button className="checkout-btn" onClick={vehicleNotFound ? () => setShowVehicleChange(true) : handleCheckout} disabled={payLoading || vehicleLoading}>{payLoading ? "Loading..." : vehicleLoading ? "Checking vehicle..." : vehicleNotFound ? "Change the registration" : "Continue to payment →"}</button>
              {brand.key === "servicebyreg" ? (
                <div className="sbr-checkout-trust">
                  <div className="sbr-checkout-pay-logos" aria-label="Accepted payment methods">
                    <span className="sbr-checkout-pay-logo sbr-checkout-pay-logo--apple"><img src="/payment-logos/apple-pay-mark.svg" alt="Apple Pay" /></span>
                    <span className="sbr-checkout-pay-logo sbr-checkout-pay-logo--gpay"><img src="/payment-logos/google-pay-mark.svg" alt="Google Pay" /></span>
                    <span className="sbr-checkout-pay-logo"><img src="/payment-logos/paypal-mark.svg" alt="PayPal" /></span>
                    <span className="sbr-checkout-pay-logo"><img src="/payment-logos/visa-mark.svg" alt="Visa" /></span>
                    <span className="sbr-checkout-pay-logo"><img src="/payment-logos/mastercard-mark.svg" alt="Mastercard" /></span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="pay-note">▣ Encrypted and securely processed</div>
                  <div className="cards"><span className="card-logo">VISA</span><span className="card-logo">Mastercard</span><span className="card-logo">AMEX</span></div>
                </>
              )}
            </aside>
          </div>
        </div>
      </main>

      {brand.key === "servicebyreg" ? (
        <SbrFooter brand={brand} />
      ) : brand.key === "servicelad" ? (
        <SlCloudFooter brand={brand} />
      ) : (
        <div className="legal-links"><a href="/about">About</a><a href="/contact">Contact</a><a href="/terms">Terms</a><a href="/privacy">Privacy</a></div>
      )}

      {paymentOpen && (
        <div className="modal open" role="dialog" aria-modal="true" onMouseDown={(event) => { if (event.target === event.currentTarget && paymentFormShown && !payLoading) setPaymentOpen(false); }}>
          <div className="modal-card">
            {paid ? (
              <div className="success" style={{ display: "block" }}><div className="success-icon">✓</div><h2>Payment confirmed</h2>{paidV5cOnly
                ? <p>V5C alerts for <strong>{displayVrm}</strong> are on. We have emailed a confirmation, and we will email you if DVLA issues a new logbook.</p>
                : <p>Your order for <strong>{displayVrm}</strong> is confirmed. A receipt and report link will be emailed to you.</p>}<a href="/">Back to homepage</a></div>
            ) : returningPayment || historyChecking ? (
              <div className="payment-return-status"><span className="payment-spinner" />{selectedPackage === "complete" && !returningPayment
                ? <><h2>Checking service records</h2><p>Your payment is only authorised. We will take it only if manufacturer service records are found.</p></>
                : <><h2>Confirming your payment</h2><p>This takes a few seconds. Please keep this page open.</p></>}</div>
            ) : partialResult ? (
              <div className="payment-return-status">
                <h2>{partialResult.serviceHistoryStatus === "no_history" ? "No service records found" : "Service records could not be confirmed"}</h2>
                <p>{partialResult.message}</p>
                <p>
                  Charged <strong>£{((partialResult.capturedPence || 0) / 100).toFixed(2)}</strong>.
                  Released <strong>£{((partialResult.releasedPence || 0) / 100).toFixed(2)}</strong>.
                  A released amount can take a few days to clear from your statement, depending on your bank.
                </p>
                {partialResult.reportToken
                  ? <a href={`/report?token=${partialResult.reportToken}`}>View your report</a>
                  : <p>Your report link will be emailed to you.</p>}
              </div>
            ) : noHistoryResult ? (
              <div className="payment-return-status">
                <h2>{noHistoryResult.status === "no_history" ? "No service records found" : "Service records could not be confirmed"}</h2>
                <p>{noHistoryResult.message} You can still buy the {riskName}.</p>
                <button type="button" onClick={chooseRiskInstead}>Choose {riskName}</button>
              </div>
            ) : returnPaymentError ? (
              <div className="payment-return-status"><h2>Checking your payment</h2><p>{returnPaymentError} If payment completed, your report link will still be emailed to you.</p><a href="/">Back to homepage</a></div>
            ) : (
              <div>
                <div className="modal-head"><div><h2>Payment details</h2><p>Secure payment via Stripe.</p></div><button className="close" onClick={() => setPaymentOpen(false)} aria-label="Close">×</button></div>
                <div className={`wallet-row${expressAvailable ? " has-wallet" : ""}`}>
                  <button
                    type="button"
                    className="paypal-btn"
                    onClick={handlePayPalPayment}
                    disabled={payLoading}
                    aria-label="Pay with PayPal"
                  >
                    <img src="/payment-logos/paypal-mark.svg" alt="" width="18" height="18" />
                    PayPal
                  </button>
                  <div className={`wallet-express${expressAvailable ? " is-ready" : ""}`}>
                    <div id="stripe-express-checkout" ref={expressCheckoutMount} />
                  </div>
                </div>
                <div className="payment-divider"><span>or pay by card</span></div>
                <div id="stripe-payment-element" ref={paymentElementMount} />
                {payError && <div className="pay-error">{payError}</div>}
                <button className="pay-btn" onClick={handleCardPayment} disabled={payLoading}>{payLoading ? "Processing..." : `${selectedPackage === "complete" ? "Authorise" : "Pay"} £${total.toFixed(2)} securely`}</button>
                {selectedPackage === "complete" && <p className="payment-hold-note">Your payment is only taken if manufacturer service records are found.</p>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
